import React, { useState } from 'react';
import { 
  XMarkIcon, 
  AcademicCapIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface NoCardsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestartProgress: () => Promise<void>;
  onDisableSpacedRepetition?: () => Promise<void>;
  deckName?: string;
  isQuickStudy?: boolean;
  nextReviewDate?: Date | null;
  cardsToReview?: number;
  spacedRepetitionEnabled?: boolean;
}

const NoCardsModal: React.FC<NoCardsModalProps> = ({
  isOpen,
  onClose,
  onRestartProgress,
  onDisableSpacedRepetition,
  deckName,
  isQuickStudy = false,
  nextReviewDate,
  cardsToReview = 0,
  spacedRepetitionEnabled = true
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationType, setConfirmationType] = useState<'restart' | 'disable-spaced' | null>(null);

  if (!isOpen) return null;

  const handleRestartProgress = async () => {
    try {
      setIsSubmitting(true);
      await onRestartProgress();
      onClose();
    } catch (error) {
      console.error('Failed to restart progress:', error);
    } finally {
      setIsSubmitting(false);
      setConfirmationType(null);
    }
  };

  const handleDisableSpacedRepetition = async () => {
    if (!onDisableSpacedRepetition) return;

    try {
      setIsSubmitting(true);
      await onDisableSpacedRepetition();
      onClose();
    } catch (error) {
      console.error('Failed to disable spaced repetition:', error);
    } finally {
      setIsSubmitting(false);
      setConfirmationType(null);
    }
  };

  const formatNextReviewDate = (date: Date | null) => {
    if (!date) return 'nunca';
    
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'ainda hoje';
    } else if (diffDays === 1) {
      return 'amanhã';
    } else if (diffDays < 7) {
      return `em ${diffDays} dias`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `em ${weeks} semana${weeks > 1 ? 's' : ''}`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `em ${months} ${months === 1 ? 'mês' : 'meses'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `em ${years} ano${years > 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
              <AcademicCapIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isQuickStudy ? 'Estudo Rápido' : 'Estudo do Flashcard'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!confirmationType ? (
            <>
              {/* Success message */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg 
                    className="h-8 w-8 text-green-600 dark:text-green-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Parabéns! Você está em dia!
                </h4>
                {(isQuickStudy || spacedRepetitionEnabled) && (
                  <p className="text-gray-600 dark:text-gray-400">
                    {isQuickStudy 
                      ? 'Nenhum card dos seus decks está disponível para estudo rápido no momento.'
                      : `Todos os cards${deckName ? ` do flashcard "${deckName}"` : ''} foram estudados recentemente.`
                    }
                  </p>
                )}
              </div>

              {/* Next review info - Only show in spaced repetition mode */}
              {!isQuickStudy && spacedRepetitionEnabled && nextReviewDate && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center mb-2">
                    <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="font-medium text-blue-900 dark:text-blue-100">
                      Próxima revisão
                    </span>
                  </div>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    {cardsToReview > 0 ? (
                      <>
                        <span className="font-semibold">{cardsToReview} card{cardsToReview > 1 ? 's' : ''}</span> 
                        {' '}{cardsToReview > 1 ? 'estarão disponíveis' : 'estará disponível'} para revisão{' '}
                        <span className="font-semibold">{formatNextReviewDate(nextReviewDate)}</span>.
                      </>
                    ) : (
                      <>
                        O próximo card estará disponível{' '}
                        <span className="font-semibold">{formatNextReviewDate(nextReviewDate)}</span>.
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Algorithm explanation - Only show in spaced repetition mode */}
              {spacedRepetitionEnabled && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                    Como funciona a repetição espaçada?
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    O algoritmo programa cards para revisão em intervalos crescentes baseados na sua performance. 
                    Cards fáceis aparecem menos frequentemente, enquanto cards difíceis são revisados mais vezes.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="space-y-3">
                {!isQuickStudy && spacedRepetitionEnabled && (
                  <button
                    type="button"
                    onClick={() => setConfirmationType('disable-spaced')}
                    className="w-full flex items-center justify-center px-4 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 font-medium rounded-lg transition-colors duration-200"
                  >
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Desativar repetição espaçada
                  </button>
                )}

                {!isQuickStudy && (
                  <button
                    type="button"
                    onClick={() => setConfirmationType('restart')}
                    className="w-full flex items-center justify-center px-4 py-3 bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Reiniciar Progresso do Flashcard
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Voltar aos Flashcards
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Confirmation screen */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {confirmationType === 'disable-spaced' ? 'Desativar Repetição Espaçada' : 'Confirmar Reinicialização'}
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {confirmationType === 'disable-spaced'
                    ? 'Ao desativar a repetição espaçada, você perderá o registro atual de revisão espaçada do deck.'
                    : `Tem certeza que deseja reiniciar o progresso${deckName ? ` do deck "${deckName}"` : ''}? `}
                </p>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    <strong>Atenção:</strong>{' '}
                    {confirmationType === 'disable-spaced'
                      ? 'Os intervalos e o histórico de revisão salvos serão apagados. Você pode reativar a repetição depois, mas o progresso atual será perdido.'
                      : 'Todo o progresso dos cards será perdido e você começará do zero. Esta ação não pode ser desfeita.'}
                  </p>
                </div>
              </div>

              {/* Confirmation buttons */}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={confirmationType === 'disable-spaced' ? handleDisableSpacedRepetition : handleRestartProgress}
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      {confirmationType === 'disable-spaced' ? 'Desativando...' : 'Reiniciando...'}
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      {confirmationType === 'disable-spaced' ? 'Sim, desativar repetição espaçada' : 'Sim, Reiniciar Progresso'}
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setConfirmationType(null)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoCardsModal;